import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Loader2 } from "lucide-react";

export default function Register() {
  const { register, error } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "dealership", company: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      navigate("/app/dashboard");
    } catch (_) {} finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-black text-white p-12 grid-pattern">
        <Link to="/" className="font-display text-2xl tracking-tighter">EAG <span className="text-[#FF2B00]">●</span></Link>
        <div>
          <h1 className="font-display text-5xl tracking-tighter leading-[1]">Onboard your<br/>dealership in minutes.</h1>
          <p className="mt-6 text-zinc-400 max-w-md">Pick your role, get your dashboard, and let the AI copilot do the heavy thinking.</p>
        </div>
        <div className="label-caps text-zinc-500">© EAG ● Dealership Services OS</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="label-caps text-[#FF2B00] mb-3">Create account</div>
          <h2 className="font-display text-4xl tracking-tighter">Register</h2>
          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label className="label-caps block mb-2">Full Name</label>
              <input
                data-testid="register-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-black px-4 py-3 focus:outline-none focus:border-[#FF2B00]"
              />
            </div>
            <div>
              <label className="label-caps block mb-2">Email</label>
              <input
                data-testid="register-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full border border-black px-4 py-3 focus:outline-none focus:border-[#FF2B00]"
              />
            </div>
            <div>
              <label className="label-caps block mb-2">Password</label>
              <input
                data-testid="register-password"
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full border border-black px-4 py-3 focus:outline-none focus:border-[#FF2B00]"
              />
            </div>
            <div>
              <label className="label-caps block mb-2">Role</label>
              <select
                data-testid="register-role"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full border border-black px-4 py-3 bg-white focus:outline-none focus:border-[#FF2B00]"
              >
                <option value="dealership">Dealership</option>
                <option value="employee">Employee</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="label-caps block mb-2">Company (optional)</label>
              <input
                data-testid="register-company"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="w-full border border-black px-4 py-3 focus:outline-none focus:border-[#FF2B00]"
              />
            </div>
            {error && <div data-testid="register-error" className="text-sm text-[#FF2B00]">{error}</div>}
            <button
              data-testid="register-submit"
              type="submit"
              disabled={busy}
              className="w-full bg-black text-white py-3 hover:bg-[#FF2B00] disabled:opacity-50 label-caps inline-flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 className="animate-spin" size={16} /> : <>Create account <ArrowRight size={16} /></>}
            </button>
          </form>
          <p className="mt-8 text-sm text-zinc-600">
            Already a member?{" "}
            <Link to="/login" data-testid="register-to-login" className="text-black underline hover:text-[#FF2B00]">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
